import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Loader2, ShoppingBag } from "lucide-react";

const EU_COUNTRIES = [
  ["FR", "France"], ["BE", "Belgique"], ["DE", "Allemagne"], ["ES", "Espagne"],
  ["IT", "Italie"], ["PT", "Portugal"], ["NL", "Pays-Bas"], ["LU", "Luxembourg"],
  ["AT", "Autriche"], ["IE", "Irlande"], ["DK", "Danemark"], ["FI", "Finlande"],
  ["SE", "Suède"], ["PL", "Pologne"], ["CZ", "Tchéquie"], ["GR", "Grèce"],
];
const OTHER_COUNTRIES = [
  ["CH", "Suisse"], ["GB", "Royaume-Uni"], ["US", "États-Unis"], ["CA", "Canada"],
  ["AU", "Australie"], ["JP", "Japon"], ["BR", "Brésil"], ["MX", "Mexique"],
];

export default function ShopCheckout() {
  const { items, totals, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: user?.email || "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    post_code: "",
    state: "",
    country: "FR",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!items.length) {
      nav("/shop/cart", { replace: true });
    }
  }, [items, nav]);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const isEU = EU_COUNTRIES.some(([c]) => c === form.country);
  const estShipping = isEU ? 6.90 : 12.90;
  const estTotal = (totals.subtotal + estShipping).toFixed(2);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ variant_id: i.variant_id, product_id: i.product_id, store_id: i.store_id, quantity: i.quantity })),
        shipping: { ...form },
        origin_url: window.location.origin,
      };
      const res = await api.post("/shop/checkout", payload);
      if (res?.data?.url) {
        // Persist that the cart has been sent to Stripe so we can clear on success page
        try { sessionStorage.setItem("geovinum_pending_session", res.data.session_id); } catch { /* ignore */ }
        window.location.href = res.data.url;
        return;
      }
      throw new Error("Aucune URL Stripe reçue");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Erreur de paiement";
      setError(String(msg));
      toast.error("Impossible de lancer le paiement", { description: String(msg) });
    } finally {
      setSubmitting(false);
    }
  };

  if (!items.length) return null;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="shop-checkout-page">
      <Link to="/shop/cart" className="inline-flex items-center gap-2 text-sm text-[#8B95A1] hover:text-[#E5276A]">
        <ArrowLeft className="w-4 h-4" /> Retour au panier
      </Link>
      <h1 className="font-display text-3xl md:text-4xl text-[#1F3A5F] mt-4">Livraison & paiement</h1>

      <form onSubmit={onSubmit} className="mt-8 grid md:grid-cols-[1fr,360px] gap-8" data-testid="shop-checkout-form">
        <div className="space-y-5">
          <Section title="Vos coordonnées">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input name="first_name" label="Prénom" value={form.first_name} onChange={update("first_name")} required />
              <Input name="last_name" label="Nom" value={form.last_name} onChange={update("last_name")} required />
            </div>
            <Input name="email" type="email" label="Email" value={form.email} onChange={update("email")} required />
            <Input name="phone" type="tel" label="Téléphone (optionnel)" value={form.phone} onChange={update("phone")} />
          </Section>

          <Section title="Adresse de livraison">
            <Input name="address_line1" label="Adresse" value={form.address_line1} onChange={update("address_line1")} required />
            <Input name="address_line2" label="Complément (optionnel)" value={form.address_line2} onChange={update("address_line2")} />
            <div className="grid sm:grid-cols-2 gap-3">
              <Input name="post_code" label="Code postal" value={form.post_code} onChange={update("post_code")} required />
              <Input name="city" label="Ville" value={form.city} onChange={update("city")} required />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input name="state" label="Région / État (optionnel)" value={form.state} onChange={update("state")} />
              <Select name="country" label="Pays" value={form.country} onChange={update("country")} required>
                <optgroup label="Union européenne">
                  {EU_COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </optgroup>
                <optgroup label="Autres pays">
                  {OTHER_COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </optgroup>
              </Select>
            </div>
          </Section>
        </div>

        {/* Summary */}
        <div className="md:sticky md:top-4 self-start">
          <div className="bg-white border border-[#1F3A5F]/12 rounded-2xl p-5">
            <h2 className="font-display text-xl text-[#1F3A5F] flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#E5276A]" /> Votre commande
            </h2>
            <div className="mt-4 space-y-2.5 max-h-64 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.variant_id} className="flex items-center gap-3 text-sm">
                  <div className="w-12 h-14 bg-[#FBF7F3] rounded overflow-hidden shrink-0">
                    {i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-[#1F3A5F] font-bold truncate leading-tight">{i.product_name}</div>
                    <div className="text-[11px] text-[#8B95A1] truncate">{i.variant_title} · ×{i.quantity}</div>
                  </div>
                  <div className="text-sm font-bold text-[#1F3A5F]">{(i.unit_price_eur * i.quantity).toFixed(2)} €</div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-[#1F3A5F]/10 text-sm space-y-1.5">
              <div className="flex justify-between text-[#4A5568]"><span>Sous-total</span><span className="text-[#1F3A5F] font-bold">{totals.subtotal.toFixed(2)} €</span></div>
              <div className="flex justify-between text-[#4A5568]"><span>Livraison {isEU ? "(UE)" : "(Monde)"}</span><span className="text-[#1F3A5F] font-bold">{estShipping.toFixed(2)} €</span></div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#1F3A5F]/10 flex justify-between items-baseline">
              <span className="font-bold text-[#1F3A5F]">Total</span>
              <span className="font-display text-2xl text-[#E5276A]" data-testid="shop-checkout-total">{estTotal} €</span>
            </div>

            {error && (
              <div className="mt-4 p-3 rounded-lg bg-[#FCE4ED] border border-[#E5276A]/30 text-xs text-[#1F3A5F]" data-testid="shop-checkout-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="shop-checkout-submit"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50 transition-colors"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirection…</> : <>Payer {estTotal} € →</>}
            </button>
            <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-[#8B95A1]">
              <ShieldCheck className="w-3 h-3 text-[#E5276A]" />
              Paiement sécurisé Stripe · 3D Secure
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#1F3A5F]/12 rounded-2xl p-5">
      <h2 className="font-display text-lg text-[#1F3A5F] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Input({ label, name, type = "text", value, onChange, required }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-[#8B95A1] uppercase tracking-wider">{label}</span>
      <input
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        required={required}
        data-testid={`shop-checkout-${name}`}
        className="mt-1 w-full px-4 py-2.5 bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] rounded-xl text-sm text-[#1F3A5F] placeholder:text-[#8B95A1] outline-none transition-colors"
      />
    </label>
  );
}

function Select({ label, name, value, onChange, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-[#8B95A1] uppercase tracking-wider">{label}</span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`shop-checkout-${name}`}
        className="mt-1 w-full px-4 py-2.5 bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] rounded-xl text-sm text-[#1F3A5F] outline-none transition-colors"
      >
        {children}
      </select>
    </label>
  );
}
